// src/components/maintenance/ProveedoresLayout.tsx

import React, { useState } from 'react';
import { Store, FileText, Package, BarChart3, Tag } from 'lucide-react';
import GestionProveedores from './GestionProveedores';
import ComparativaPrecios from './ComparativaPrecios';
import CotizacionPaquete from './CotizacionPaquete';
import ResumenPaquetes from './ResumenPaquetes';
import CatalogoItems from './CatalogoItems';

type TabType = 'listado' | 'catalogo' | 'comparativa' | 'cotizacion' | 'resumen';

const ProveedoresLayout: React.FC = () => {
    const [activeTab, setActiveTab] = useState<TabType>('listado');

    const tabs = [
        {
            id: 'listado' as TabType,
            label: 'Proveedores',
            labelFull: 'Listado de Proveedores',
            icon: Store,
            color: 'indigo',
            component: GestionProveedores
        },
        {
            id: 'catalogo' as TabType,
            label: 'Catálogo',
            labelFull: 'Catálogo de Ítems',
            icon: Tag,
            color: 'violet',
            component: CatalogoItems
        },
        {
            id: 'comparativa' as TabType,
            label: 'Comparativa',
            labelFull: 'Comparativa de Precios',
            icon: BarChart3,
            color: 'blue',
            component: ComparativaPrecios
        },
        {
            id: 'cotizacion' as TabType,
            label: 'Cotización',
            labelFull: 'Cotización por Paquete',
            icon: FileText,
            color: 'cyan',
            component: CotizacionPaquete
        },
        {
            id: 'resumen' as TabType,
            label: 'Paquetes',
            labelFull: 'Comparativa de Paquetes',
            icon: Package,
            color: 'purple',
            component: ResumenPaquetes
        }
    ];

    // Keep track of which tabs have been visited to mount them lazily
    const [visitedTabs, setVisitedTabs] = useState<Set<TabType>>(new Set([activeTab]));

    const handleTabChange = (tabId: TabType) => {
        setActiveTab(tabId);
        setVisitedTabs(prev => new Set(prev).add(tabId));
    };

    const activeTabData = tabs.find(t => t.id === activeTab);

    return (
        <div className="h-full flex flex-col bg-gray-50/80">
            {/* Header — premium gradient */}
            <div className="bg-white border-b border-gray-200/80 shadow-sm">
                {/* Title row */}
                <div className="px-6 pt-5 pb-0">
                    <div className="flex-1 min-w-0 mb-4">
                        <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                            Gestión de Proveedores
                        </h1>
                        <p className="text-sm text-gray-500 font-medium mt-1">
                            {activeTabData?.labelFull ?? 'Administración de proveedores y lista de precios'}
                        </p>
                    </div>

                    {/* Tabs Navigation */}
                    <div className="flex gap-1 overflow-x-auto scrollbar-none -mb-px">
                        {tabs.map((tab) => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.id;

                            return (
                                <button
                                    key={tab.id}
                                    onClick={() => handleTabChange(tab.id)}
                                    className={`
                                        group relative flex items-center gap-2 px-4 py-2.5 text-sm font-semibold
                                        border-b-2 transition-all whitespace-nowrap shrink-0
                                        ${isActive
                                            ? 'border-indigo-600 text-indigo-600'
                                            : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300'
                                        }
                                    `}
                                >
                                    <Icon
                                        size={15}
                                        className={`transition-colors ${isActive ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-600'}`}
                                    />
                                    {/* Short label on mobile, full on tablet+ */}
                                    <span className="sm:hidden">{tab.label}</span>
                                    <span className="hidden sm:inline">{tab.labelFull}</span>

                                    {isActive && (
                                        <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-indigo-600 rounded-t-full" />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Content Area - Keep-Alive Implementation */}
            <div className="flex-1 overflow-auto">
                {tabs.map((tab) => {
                    const Component = tab.component;
                    if (!visitedTabs.has(tab.id)) return null;

                    return (
                        <div
                            key={tab.id}
                            className={`h-full ${activeTab === tab.id ? 'block' : 'hidden'}`}
                        >
                            <Component />
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default ProveedoresLayout;
